/**
 * CLINIC-DAY-SPINE-V1 -- the leads-workspace "find by phone" door model
 * (founder ruling 2026-08-06, R-CD-B1 under R-CD-R1 SEARCH-FIRST ONE
 * DOOR; pure -- no React, no fetch).
 *
 * STANDING LAW (R-CD-R1): creation is exclusively the NOT-FOUND outcome of
 * search. The evidence machine below is the same structural gate the
 * reception register modal carries (globalRegisterEvidence,
 * frontend/src/dashboard/patients/global-search-model.ts) applied to the
 * LEAD side: no search evidence -> no create door. It is a UI gate, never
 * the security boundary (the server route re-validates everything).
 *
 * UMR IS THE BADGE (R-CD-R2-L2): a result with a UMR is a patient; a
 * result without one is by definition still a lead. The cards carry that
 * one differentiator and NOTHING else -- no invented lead/patient chips,
 * labels, or colors on this surface.
 */

export const LEAD_SEARCH_OPEN_ACTION = 'Find by phone';
export const LEAD_SEARCH_TITLE = 'Find by phone';
export const LEAD_SEARCH_PLACEHOLDER = 'Phone, name, or ID';
export const LEAD_SEARCH_BUTTON = 'Search';
export const LEAD_CREATE_ACTION = 'Create new enquiry';
export const LEAD_CREATE_TITLE = 'New enquiry';
export const LEAD_OPEN_ENQUIRY_ACTION = 'Open enquiry';
export const LEAD_NONE_OF_THESE = 'None of these';
export const LEAD_SEARCH_UNAVAILABLE =
  'Search is not available right now. Try again, or refresh the page.';
export const LEAD_PHONE_FIRST_HINT =
  'Search by phone number to create a new enquiry.';

export function leadSearchNoRecordLine(q: string): string {
  return `No record found for ${q}.`;
}

export interface LeadEnquiryEvidenceState {
  readonly searched: boolean;
  readonly searchFailed: boolean;
  /** Patient candidates + enquiry cards, together. */
  readonly resultCount: number;
  readonly noneOfThese: boolean;
  /** The create door is phone-first (R-CD-R1): only a PHONE-mode search
   *  can birth an enquiry with its number pre-filled and read-only. */
  readonly searchMode: 'phone' | 'umr' | 'name' | null;
}

export type LeadEnquiryEvidence =
  | 'search-failed'
  | 'no-match'
  | 'none-of-these'
  | 'phone-first-hint'
  | null;

/** The structural gate (the globalRegisterEvidence shape, lead side):
 *  null = no create door renders. 'phone-first-hint' = a miss on a
 *  non-phone search explains the law instead of opening the door. */
export function leadEnquiryEvidence(
  state: LeadEnquiryEvidenceState,
): LeadEnquiryEvidence {
  if (state.searchFailed) return 'search-failed';
  if (!state.searched) return null;
  if (state.resultCount === 0) {
    return state.searchMode === 'phone' ? 'no-match' : 'phone-first-hint';
  }
  if (state.noneOfThese) {
    return state.searchMode === 'phone' ? 'none-of-these' : 'phone-first-hint';
  }
  return null;
}

export interface LeadEnquiryFormState {
  readonly phone: string;
  readonly name: string;
  readonly treatmentType: string;
  readonly source: string;
  readonly clinicId: number | null;
}

export function validateLeadEnquiryForm(
  form: LeadEnquiryFormState,
): readonly string[] {
  const errors: string[] = [];
  if (form.phone.trim() === '') errors.push('Phone is required.');
  if (form.name.trim() === '') errors.push('Name is required.');
  if (form.treatmentType.trim() === '')
    errors.push('Pick what they asked about.');
  if (form.source.trim() === '') errors.push('Pick the source / channel.');
  if (form.clinicId === null) errors.push('Pick the clinic.');
  return errors;
}
